import { graphqlPost } from "api/graphqlClient";
import {
    executionsGradualQuery,
    executionsPlainListSubscription,
} from "containers/Executions/Executions.type";
import { useCallback, useEffect, useRef, useState } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import {
    EXECUTION_GRADUAL_ORDER,
    EXECUTION_GRADUAL_PAGE_SIZE,
    EXECUTION_LIVE_WINDOW_LIMIT,
    ExecutionCursorInput,
    GradualExecutionPage,
    GradualExecutionRow,
    appendOlderExecutions,
    executionPageRows,
    mergeLiveExecutions,
    nextExecutionCursor,
} from "./gradualExecutions";

export interface UseGradualExecutionsArgs {
    /** Assembled filter JSON string (base scope + user filter), or null. */
    filter: string | null;
    /** Transport flags for trash visibility (sent as query params / socket params). */
    allowDeleted: boolean;
    allowNormal: boolean;
    /** When false nothing is fetched or subscribed (e.g. the classic offset view
     *  is active, or live updates are toggled off and the view is frozen). */
    enabled: boolean;
    /** Gate for the live subscription only. */
    live: boolean;
}

export interface UseGradualExecutionsResult {
    rows: GradualExecutionRow[];
    isScanning: boolean;
    moreAvailable: boolean;
    error: Error | null;
    loadOlder: () => void;
    cancel: () => void;
    reload: () => void;
}

/**
 * Executions monitor data source for the default newest-first view: the initial
 * screenful and each "load older" step come from the keyset `execution_list_gradual`
 * query (no COUNT); an `execution_plain_list` subscription over the newest window
 * keeps the top live, merged by id (see mergeLiveExecutions). Modelled on the
 * Logs tab's useGradualRecordLogs. See
 * operator-task-fe-executions-monitor-gradual-endless-scroll.
 */
export function useGradualExecutions({
    filter,
    allowDeleted,
    allowNormal,
    enabled,
    live,
}: UseGradualExecutionsArgs): UseGradualExecutionsResult {
    const [rows, setRows] = useState<GradualExecutionRow[]>([]);
    const [isScanning, setIsScanning] = useState(false);
    const [moreAvailable, setMoreAvailable] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const genRef = useRef(0);
    const abortRef = useRef<AbortController | null>(null);
    const cursorRef = useRef<ExecutionCursorInput | null>(null);
    const rowsRef = useRef<GradualExecutionRow[]>([]);
    const liveGenRef = useRef(0);

    const fetchPage = useCallback(async () => {
        if (!enabled) {
            return;
        }
        const gen = genRef.current;
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setIsScanning(true);
        setError(null);
        const isStale = () => controller.signal.aborted || gen !== genRef.current;
        try {
            const response = (await graphqlPost(
                {
                    query: executionsGradualQuery,
                    variables: {
                        filter,
                        limit: EXECUTION_GRADUAL_PAGE_SIZE,
                        continue_before: cursorRef.current ?? undefined,
                    },
                    allow_deleted: allowDeleted,
                    allow_normal: allowNormal,
                },
                { signal: controller.signal }
            )) as { data?: { execution_list_gradual?: GradualExecutionPage } };
            if (isStale()) {
                return;
            }
            const page: GradualExecutionPage = response?.data?.execution_list_gradual ?? {};
            const merged = appendOlderExecutions(rowsRef.current, executionPageRows(page));
            rowsRef.current = merged;
            cursorRef.current = nextExecutionCursor(page);
            setRows(merged);
            setMoreAvailable(Boolean(page.more_available) && cursorRef.current !== null);
        } catch (err) {
            if (isStale()) {
                return;
            }
            setError(err instanceof Error ? err : new Error(String(err)));
        } finally {
            if (gen === genRef.current) {
                setIsScanning(false);
            }
        }
    }, [enabled, filter, allowDeleted, allowNormal]);

    const resetAndFetch = useCallback(() => {
        genRef.current += 1;
        abortRef.current?.abort();
        abortRef.current = null;
        rowsRef.current = [];
        cursorRef.current = null;
        setRows([]);
        setMoreAvailable(false);
        setError(null);
        if (enabled) {
            void fetchPage();
        } else {
            setIsScanning(false);
        }
    }, [enabled, fetchPage]);

    // Restart from the newest rows whenever the query inputs change.
    useEffect(() => {
        resetAndFetch();
        return () => {
            abortRef.current?.abort();
        };
    }, [resetAndFetch]);

    const loadOlder = useCallback(() => {
        if (cursorRef.current === null) {
            return;
        }
        void fetchPage();
    }, [fetchPage]);

    const cancel = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
        setIsScanning(false);
    }, []);

    const applyLiveRows = useCallback((incoming: GradualExecutionRow[]) => {
        const merged = mergeLiveExecutions(rowsRef.current, incoming, EXECUTION_LIVE_WINDOW_LIMIT);
        if (merged === rowsRef.current) {
            return;
        }
        rowsRef.current = merged;
        setRows(merged);
    }, []);

    // Live top-of-list updates. The subscription re-pushes the newest window on
    // every execution NOTIFY; each push is merged into the accumulated list so
    // loaded older rows are kept.
    useEffect(() => {
        if (!enabled || !live) {
            return undefined;
        }
        const typedWindow: any = window;
        const websocketHost =
            typedWindow.REACT_APP_WEBSOCKET_API || `wss://${typedWindow.location.host}`;
        const websocketParams = {
            subscription: executionsPlainListSubscription,
            variables: {
                filter,
                limit: EXECUTION_LIVE_WINDOW_LIMIT,
                offset: 0,
                order: EXECUTION_GRADUAL_ORDER,
            },
            allow_deleted: allowDeleted,
            allow_normal: allowNormal,
        };
        const myGen = (liveGenRef.current += 1);
        let socket: ReconnectingWebSocket | null = new ReconnectingWebSocket(
            `${websocketHost}${typedWindow.REACT_APP_WEBSOCKET_API_NAMESPACE}/graphql?subscription_message=true`
        );
        const handleMessage = (event: MessageEvent) => {
            if (myGen !== liveGenRef.current) {
                return;
            }
            let response: any;
            try {
                response = JSON.parse(event.data);
            } catch {
                return;
            }
            // The gradual read owns error surfacing; a bad live push is skipped.
            if (response?.BadRequest || response?.InternalServerError || response?.errors) {
                return;
            }
            const list: unknown =
                response?.data?.execution_plain_list ?? response?.execution_plain_list;
            if (Array.isArray(list)) {
                applyLiveRows(list.filter(Boolean) as GradualExecutionRow[]);
            }
        };
        socket.addEventListener("open", () => {
            socket?.send(JSON.stringify(websocketParams));
        });
        socket.addEventListener("message", handleMessage);
        return () => {
            liveGenRef.current += 1;
            socket?.close();
            socket = null;
        };
    }, [enabled, live, filter, allowDeleted, allowNormal, applyLiveRows]);

    return {
        rows,
        isScanning,
        moreAvailable,
        error,
        loadOlder,
        cancel,
        reload: resetAndFetch,
    };
}

export default useGradualExecutions;
